import React, { useState } from 'react'

import {
  Action,
  CustomField,
  DateTimeField,
  FlagsField,
  FieldSpec,
  LinkedField,
  PaginatedTable,
  RelatedLinkedField,
  ValueField,
  StatusField,
} from 'src/components/Table'
import { ParcelOrder, ParcelOrderStatusEnum, nextOrderStatus } from 'src/api/models'
import { OrderLabel } from 'src/modules/supply_chain/components/parcel/OrderLabel'
import { PickTicket } from 'src/modules/supply_chain/components/parcel/PickTicket'
import { BulkPrint } from 'src/components/Print/Bulk'
import { ClientFilter, FlagFilter } from 'src/modules/shared/components/filters'
import { useAsyncPrint } from 'src/components/ToPrint/useAsyncPrint'
import { skuit } from 'src/services/skuit'
import { ListTableProps } from './types'

const fieldOptions: { [key: string]: FieldSpec } = {
  number: { id: 'number', label: 'Number', renderer: LinkedField(ValueField) },
  client: { id: 'client', label: 'Client', ordering: 'account__name', renderer: RelatedLinkedField(ValueField, 'name') },
  placedOn: {
    id: 'placedOn',
    label: 'Date Placed',
    ordering: 'placed_on',
    renderer: DateTimeField,
  },
  fulfillOn: {
    id: 'fulfillOn',
    label: 'Date to Fulfill On',
    ordering: 'fulfill_on',
    renderer: DateTimeField,
  },
  consignee: { id: 'address.consignee', label: 'Consignee', renderer: ValueField },
  status: { id: 'status', label: 'Status', renderer: StatusField },
  flags: { id: 'flags', label: 'Flags', renderer: FlagsField },
  customFields: { id: 'customFields', label: 'Custom Fields', renderer: CustomField },
}

export const ParcelOrderListTable = (props: ListTableProps) => {
  const { paginator, fields, bulkActions, title = 'Parcel Orders' } = props
  const [selected, setSelected] = useState<ParcelOrder[]>([])
  const { print, renderPrintComponent } = useAsyncPrint()

  const allOrdersHaveSameStatus = selected.every((order) => order.status === selected[0]?.status)

  const bulkOptions: { [key: string]: Action } = {
    printOrder: {
      label: 'Print Order',
      action: () => {
        print((props) => (
          <BulkPrint
            {...props}
            items={selected}
            render={({ obj, onReadyToPrint }) => {
              onReadyToPrint?.()
              return <OrderLabel order={obj} />
            }}
          />
        ))
      },
    },
    printPickTicket: {
      label: 'Print Pick-Ticket',
      action: () => {
        print((props) => (
          <BulkPrint
            {...props}
            items={selected}
            render={({ obj, onReadyToPrint }) => {
              return <PickTicket onReadyToPrint={onReadyToPrint} order={obj} />
            }}
          />
        ))
      },
    },
    printAll: {
      label: 'Print all',
      action: () => {
        print((props) => (
          <BulkPrint
            {...props}
            items={selected}
            render={({ obj, onReadyToPrint }) => {
              return (
                <>
                  <OrderLabel order={obj} />
                  <PickTicket onReadyToPrint={onReadyToPrint} order={obj} />
                </>
              )
            }}
          />
        ))
      },
    },
    divider: {
      label: 'divider',
      action: () => {
        // this is a divider for visual organization purposes
      },
    },
    status: {
      label:
        selected.length > 0 && selected[0].status && allOrdersHaveSameStatus
          ? `Set order${selected.length === 1 ? '' : 's'} as ${nextOrderStatus(selected[0].status)}`
          : "Can't update status",
      action: async (orders: ParcelOrder[]) => {
        if (orders[0].status) {
          const newStatus = nextOrderStatus(orders[0].status)
          await Promise.all(
            orders.map((order) => {
              if (order.guid) {
                order.status = newStatus
                return skuit.sc.parcel.orders.patch(order.guid, { status: newStatus })
              }
              return new Promise(() => {})
            })
          )
          paginator.refresh()
        }
      },
      isEnabled: allOrdersHaveSameStatus,
    },
    cancel: {
      label: `Cancel order${selected.length === 1 ? '' : 's'}`,
      action: async (orders: ParcelOrder[]) => {
        await Promise.all(
          orders.map((order) => {
            if (order.guid) {
              return skuit.sc.parcel.orders.patch(order.guid, {
                status: ParcelOrderStatusEnum.canceled,
              })
            }
            return new Promise(() => {})
          })
        )
        paginator.refresh()
      },
    },
  }

  return (
    <>
      {renderPrintComponent()}
      <PaginatedTable
        title={title}
        fields={fields.map((field) => fieldOptions[field])}
        paginator={paginator}
        onSelectedItem={setSelected}
        bulkActions={bulkActions?.map((action) => bulkOptions[action])}
        filters={[
          {
            id: 'clients',
            label: 'Clients',
            widget: ClientFilter,
          },
          {
            id: 'statuses',
            label: 'Status',
            options: ParcelOrderStatusEnum,
          },
          {
            id: 'flags',
            label: 'Flags',
            widget: FlagFilter,
          },
        ]}
      />
    </>
  )
}
